import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useGetSessionQuery, useLoginMutation, useLogoutMutation } from '../api/auth.api';
import type { User } from '../api/models/user.model';
import { useGetCurrentUserQuery } from '../api/user.api';
import { useDispatch } from 'react-redux';
import { setLoginRedirectUrl } from '../redux/slices/uiSettingsSlice';
import { apiConfig } from '../config/api.config';

type AuthContextType = {
	user: User | null;
	isAuthenticated: boolean;
	loading: boolean;
	error: string | null;
	login: (email: string, password: string) => Promise<void>;
	logout: () => Promise<void>;
	refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const loginRedirectUrl = apiConfig.loginRedirectUrl;

	const [error, setError] = useState<string | null>(null);
	const dispatch = useDispatch();

	const {
		data: session,
		isLoading: _sessionLoading,
		refetch,
		error: sessionError,
	} = useGetSessionQuery();
	const [loginMutation, { isLoading: loginLoading }] = useLoginMutation();
	const [logoutMutation, { isLoading: logoutLoading }] = useLogoutMutation();

	useEffect(() => {
		const isUnauthorized =
			sessionError &&
			'status' in sessionError &&
			(sessionError.status === 401 || sessionError.status === 'PARSING_ERROR');
		const isRedirect = sessionError && 'status' in sessionError && sessionError.status === 302;

		if (isRedirect || isUnauthorized) {
			/* 
				User Story 2766337:
				Switching to using build-time variable for security (was also not able to find any reference to the API returning `redirectUrl` anyways )
			*/
			// const loginUrl = (sessionError as any)?.data?.redirectUrl || loginRedirectUrl;
			dispatch(setLoginRedirectUrl(loginRedirectUrl));
		}
	}, [sessionError, dispatch, loginRedirectUrl]);

	// --- Begin stub for user roles ---
	const isAuthenticated = !!session?.authenticated;
	const { data: user, isLoading: userLoading } = useGetCurrentUserQuery(undefined, {
		skip: !isAuthenticated,
	});
	// --- End stub for user roles ---

	const loading = _sessionLoading || userLoading || loginLoading || logoutLoading;

	const login = async () => {
		setError(null);
		try {
			await loginMutation().unwrap();
			await refetch();
		} catch (err: any) {
			setError(err?.data?.message || 'Login failed');
		}
	};

	const logout = async () => {
		setError(null);
		try {
			await logoutMutation().unwrap();
			await refetch();
		} catch (err: any) {
			setError(err?.data?.message || 'Logout failed');
		}
	};

	const refresh = async () => {
		setError(null);
		try {
			await refetch();
		} catch (err: any) {
			setError('Session refresh failed');
		}
	};

	const value = useMemo(() => {
		// Compose a User object for context (unchanged)
		const userObj: User | null = session?.email
			? {
					emailAddress: session.email,
					userId: user?.userId ?? 0,
					id: '',
					firstName: '',
					lastName: '',
					roles: user?.roles ?? [],
				}
			: null;
		return {
			user: userObj,
			isAuthenticated: !!session?.authenticated,
			loading,
			error,
			login,
			logout,
			refresh,
		};
	}, [session, loading, error, user]);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook for consuming AuthContext
export function useAuth() {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error('useAuth must be used within AuthProvider');
	return ctx;
}
